import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  requiredPermission?: string;
  requiredPermissions?: string[];
  requireAll?: boolean; // true = ต้องมีครบทุกสิทธิ์, false = มีอย่างน้อย 1 สิทธิ์
  requireAuth?: boolean;
}

/**
 * ProtectedRoute Component
 *
 * Component สำหรับป้องกันการเข้าถึง routes ที่ต้องการ permission
 * - ตรวจสอบว่า user login แล้วหรือยัง
 * - ตรวจสอบว่า user มี permission ที่ต้องการหรือไม่
 * - ถ้าไม่มีสิทธิ์ ให้ redirect ไปหน้า Access Denied
 *
 * @param requiredPermission - Permission เดียวที่ต้องการ (string)
 * @param requiredPermissions - หลาย permissions ที่ต้องการ (array of strings)
 * @param requireAll - ต้องมีครบทุกสิทธิ์หรือไม่ (default: true)
 * @param requireAuth - ต้องการให้ login หรือไม่ (default: true)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredPermission,
  requiredPermissions,
  requireAll = true,
  requireAuth = true,
}) => {
  const { isAuthenticated, isInitializing, hasPermission, user } = useAuth();
  const location = useLocation();

  // แสดงตัวโหลดขณะกำลังตรวจสอบสถานะการยืนยันตัวตน (เฉพาะตอน bootstrap — ดู BUG-AUTH-REMOUNT)
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // ถ้าต้องการยืนยันตัวตนแต่ผู้ใช้ยังไม่ได้ยืนยันตัวตน ให้เปลี่ยนเส้นทางไปยังหน้าล็อกอิน
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ถ้ามีการกำหนด permission ให้ตรวจสอบ
  if (isAuthenticated && (requiredPermission || requiredPermissions)) {
    // Super Admin/Super User มีสิทธิ์ทั้งหมด
    // G-02: bypass model เดียวกันทั้ง 4 ชั้น (ProtectedRoute, AuthContext.hasPermission,
    // AuthGuard, buildMenuAccessChecker) — ตรงกับ backend core/permissions.py ที่ bypass บน is_superuser
    const isSuperAdmin = user && (user.is_superadmin || user.is_superuser || user.role === 'superadmin');

    if (!isSuperAdmin) {
      let hasRequiredPermission = false;

      // กรณีที่ระบุ requiredPermission (เดี่ยว)
      if (requiredPermission) {
        hasRequiredPermission = hasPermission(requiredPermission);
      }
      // กรณีที่ระบุ requiredPermissions (หลายตัว)
      else if (requiredPermissions && requiredPermissions.length > 0) {
        if (requireAll) {
          // ต้องมีครบทุกสิทธิ์
          hasRequiredPermission = requiredPermissions.every(p => hasPermission(p));
        } else {
          // มีอย่างน้อย 1 สิทธิ์
          hasRequiredPermission = requiredPermissions.some(p => hasPermission(p));
        }
      }

      if (!hasRequiredPermission) {
        // ไม่มีสิทธิ์เข้าถึง - redirect ไปหน้า Access Denied
        return <Navigate to="/access-denied" state={{ from: location }} replace />;
      }
    }
  }

  // ถ้าผ่านการตรวจสอบทั้งหมด แสดงคอมโพเนนต์ที่ป้องกัน
  return <Outlet />;
};

export default ProtectedRoute;
